import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Message } from "primereact/message";
import { Tag, TagProps } from "primereact/tag";
import { Toast } from "primereact/toast";
import { RefObject, useEffect, useRef, useState } from "react";

import { parseDate } from "../../../helpers/date";
import { useProcessEdiSplitPiecesMutation } from "../../../store/queries/ediConversion";
import {
  EdiSplitBatchStatus,
  IEdiSplitBatch,
  IEdiSplitPiece,
} from "../../../types/ediConversionFile";
import {
  isHeld,
  isSplitBatchActive,
  isSplitPieceInFlight,
} from "./ediFileStatus";
import EdiFileStatusLabel from "./EdiFileStatusLabel";
import { useEdiSplits } from "./useEdiSplits";

const BATCH_STATUS: Record<
  EdiSplitBatchStatus,
  Pick<TagProps, "severity" | "icon" | "style"> & { label: string }
> = {
  queued: { label: "Queued", style: { background: "var(--gray-500)" } },
  running: {
    label: "Splitting…",
    severity: "info",
    icon: "pi pi-spin pi-spinner",
  },
  completed: { label: "Completed", severity: "success" },
  failed: { label: "Failed", severity: "danger" },
};

// A failed attempt whose job is queued/running again: not a failure yet, so not red.
const RETRYING_STATUS: Pick<TagProps, "severity" | "icon"> & { label: string } =
  {
    label: "Retrying…",
    severity: "info",
    icon: "pi pi-spin pi-spinner",
  };

// A piece can be processed only once its split has completed: until then the source file may still
// exist, and the API refuses (409) to process a piece while its source exists.
const PIECE_NOT_PROCESSABLE_TOOLTIP = "Available once the split has completed";

// A processed piece can be reprocessed like any conversion file, once its last run has settled.
const PIECE_NOT_REPROCESSABLE_TOOLTIP =
  "Available once the piece has finished processing";

const heldPiecesOf = (batch: IEdiSplitBatch): IEdiSplitPiece[] =>
  batch.pieces.filter(isHeld);

const processedCountOf = (batch: IEdiSplitBatch): number =>
  batch.pieces.filter((p) => p.claimProcessingStatus === "completed").length;

const plural = (n: number, one: string, many: string) =>
  `${n.toLocaleString()} ${n === 1 ? one : many}`;

interface IEdiSplitFilesTabProps {
  // The EDI Files toast, so messages survive switching sub-tabs.
  toast: RefObject<Toast>;
  // Opens the "Split 835 file into smaller files" dialog.
  onSplitFile: () => void;
  // The files table's own handlers, so a piece is processed, reprocessed, downloaded and deleted
  // exactly the way any other conversion file is.
  onProcessHeldFile: (id: string) => void;
  onReprocessFile: (id: string, displayName: string) => void;
  onDownloadOriginal: (id: string) => void;
  onDeleteFile: (id: string, displayName: string) => void;
}

/**
 * CLE-2921 — the "Split 835 Files" sub-tab (SuperAdmin). Lists split operations newest first; each
 * row expands to its pieces. Pieces that are on hold can be processed one at a time ("Process next
 * piece", or "Process now" on a piece) or all at once; a processed piece can be reprocessed on its
 * own. Polls while anything is splitting or processing.
 */
function EdiSplitFilesTab({
  toast,
  onSplitFile,
  onProcessHeldFile,
  onReprocessFile,
  onDownloadOriginal,
  onDeleteFile,
}: IEdiSplitFilesTabProps) {
  const { batches, isLoading, isError, isLoaded } = useEdiSplits();
  const [processPieces] = useProcessEdiSplitPiecesMutation();
  // `${batchId}:${next|all}` while a batch-level start request is in flight.
  const [startingKey, setStartingKey] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const seenBatchIds = useRef<Set<string> | null>(null);

  // Open the newest split on first load, and any split that appears while the tab is open (the one
  // just started from the dialog), so its pieces show up without a click.
  useEffect(() => {
    if (!isLoaded) return;
    const seen = seenBatchIds.current;
    if (seen === null) {
      seenBatchIds.current = new Set(batches.map((b) => b.id));
      if (batches[0]) setExpandedRows({ [batches[0].id]: true });
      return;
    }
    const fresh = batches.filter((b) => !seen.has(b.id));
    if (fresh.length === 0) return;
    fresh.forEach((b) => seen.add(b.id));
    setExpandedRows((prev) =>
      fresh.reduce((acc, b) => ({ ...acc, [b.id]: true }), prev)
    );
  }, [batches, isLoaded]);

  const startPieces = async (batch: IEdiSplitBatch, pieces: "next" | "all") => {
    setStartingKey(`${batch.id}:${pieces}`);
    try {
      const result = await processPieces({
        batchId: batch.id,
        pieces,
      }).unwrap();
      const { started } = result.data;
      if (started.length === 0) {
        toast.current?.show({
          severity: "info",
          summary: "Nothing to process",
          detail: "No pieces of this split are on hold.",
        });
      } else if (pieces === "next") {
        const piece = batch.pieces.find((p) => p.id === started[0]);
        toast.current?.show({
          severity: "success",
          summary: "Processing started",
          detail: piece
            ? `Processing piece ${piece.splitIndex} of ${
                batch.pieceCount ?? batch.pieces.length
              }.`
            : "Processing the next piece.",
        });
      } else {
        toast.current?.show({
          severity: "success",
          summary: "Processing started",
          detail: `Processing ${plural(started.length, "piece", "pieces")}.`,
        });
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Could not start processing",
        detail:
          error?.data?.message ||
          error?.data?.messages?.[0] ||
          "Failed to start processing.",
        life: 8000,
      });
    } finally {
      setStartingKey(null);
    }
  };

  const confirmProcessAll = (batch: IEdiSplitBatch, heldCount: number) =>
    confirmDialog({
      message:
        heldCount === 1
          ? "Process the 1 remaining piece now?"
          : `Process all ${heldCount.toLocaleString()} remaining pieces now?`,
      header: "Process all remaining",
      icon: "pi pi-forward",
      acceptLabel: "Process all",
      rejectLabel: "Cancel",
      accept: () => startPieces(batch, "all"),
    });

  // ── Batch columns ──
  const sourceBody = (b: IEdiSplitBatch) => (
    <div>
      <div className="font-medium">{b.sourceDisplayName}</div>
      <small className="text-500">
        {b.sourceFileName} · {plural(b.sourceRecordCount, "record", "records")}
      </small>
    </div>
  );

  const providerBody = (b: IEdiSplitBatch) =>
    b.sourceProviderName ?? <span className="text-500">—</span>;

  const maxRecordsBody = (b: IEdiSplitBatch) =>
    b.recordsPerPiece.toLocaleString();

  const piecesBody = (b: IEdiSplitBatch) => {
    if (isSplitBatchActive(b)) {
      return (
        <span className="inline-flex align-items-center gap-2 text-600 white-space-nowrap">
          <i className="pi pi-spin pi-spinner" />
          Splitting…
        </span>
      );
    }
    if (b.pieceCount === null) return <span className="text-500">—</span>;
    const heldCount = heldPiecesOf(b).length;
    return (
      <div>
        <div className="white-space-nowrap">
          {processedCountOf(b).toLocaleString()} of{" "}
          {b.pieceCount.toLocaleString()} processed
        </div>
        {heldCount > 0 && (
          <small className="text-500">
            {heldCount.toLocaleString()} on hold
          </small>
        )}
      </div>
    );
  };

  const statusBody = (b: IEdiSplitBatch) => {
    const { label, ...tag } = b.retryPending
      ? RETRYING_STATUS
      : BATCH_STATUS[b.status];
    return (
      <div>
        <Tag value={label} {...tag} />
        {b.status === "failed" && b.error && (
          // While a retry is pending the last attempt's error is informational, so it is muted.
          <small
            className={`block mt-1 ${
              b.retryPending ? "text-600" : "text-red-600"
            }`}
            style={{ maxWidth: "22rem", whiteSpace: "normal" }}
          >
            {b.error}
          </small>
        )}
      </div>
    );
  };

  const createdByBody = (b: IEdiSplitBatch) =>
    b.createdByName ?? <span className="text-500">—</span>;

  const dateBody = (b: IEdiSplitBatch) => parseDate(b.createdAt);

  const batchActionsBody = (b: IEdiSplitBatch) => {
    const held = heldPiecesOf(b);
    if (b.status !== "completed" || held.length === 0) return null;
    const total = b.pieceCount ?? b.pieces.length;
    return (
      <div className="flex gap-2 flex-wrap">
        <Button
          label="Process next piece"
          icon="pi pi-step-forward"
          className="p-button-outlined p-button-sm"
          onClick={() => startPieces(b, "next")}
          loading={startingKey === `${b.id}:next`}
          disabled={startingKey !== null}
          tooltip={`Process piece ${held[0].splitIndex} of ${total}`}
          tooltipOptions={{ position: "top" }}
        />
        <Button
          label="Process all remaining"
          icon="pi pi-forward"
          className="p-button-outlined p-button-sm"
          onClick={() => confirmProcessAll(b, held.length)}
          loading={startingKey === `${b.id}:all`}
          disabled={startingKey !== null}
        />
      </div>
    );
  };

  // ── Piece columns (the expanded row) ──
  const pieceRecordsBody = (p: IEdiSplitPiece) =>
    p.uploadStatus === "parsed" ? (
      p.recordCount.toLocaleString()
    ) : (
      <span className="text-500">
        planned {p.plannedRecordCount.toLocaleString()}
      </span>
    );

  const pieceStatusBody = (p: IEdiSplitPiece) => (
    <EdiFileStatusLabel status={isHeld(p) ? "held" : p.claimProcessingStatus} />
  );

  // When the piece was last reprocessed — blank until its first reprocess. With the time, because
  // the same pieces are reprocessed several times a day.
  const pieceLastProcessedBody = (p: IEdiSplitPiece) =>
    p.lastReprocessedAt ? (
      parseDate(p.lastReprocessedAt, true, true, "MM/DD/YYYY h:mm A")
    ) : (
      <span className="text-500">—</span>
    );

  const pieceActionsBody = (p: IEdiSplitPiece, batch: IEdiSplitBatch) => {
    const processable = batch.status === "completed";
    // The files table's Reprocess rule: a file whose last run settled, completed or failed.
    const inFlight = isSplitPieceInFlight(p);
    const reprocessable =
      !inFlight &&
      (p.claimProcessingStatus === "completed" ||
        p.claimProcessingStatus === "failed");
    let reprocessTooltip = PIECE_NOT_REPROCESSABLE_TOOLTIP;
    if (inFlight) {
      reprocessTooltip = "Reprocessing…";
    } else if (reprocessable) {
      reprocessTooltip = "Reprocess";
    }
    return (
      <div className="flex align-items-center gap-1">
        {isHeld(p) && (
          // A React-owned wrapper: with showOnDisabled, PrimeReact's Tooltip moves the button into a
          // <span> it creates outside React, after which React cannot remove the button on its own
          // (it goes when the piece starts processing). Removing this span takes the whole subtree.
          <span>
            <Button
              label="Process now"
              icon="pi pi-play"
              className="p-button-text p-button-sm"
              onClick={processable ? () => onProcessHeldFile(p.id) : undefined}
              disabled={!processable}
              tooltip={processable ? undefined : PIECE_NOT_PROCESSABLE_TOOLTIP}
              tooltipOptions={{ position: "top", showOnDisabled: true }}
            />
          </span>
        )}
        {!isHeld(p) && (
          // The same React-owned wrapper as "Process now", for the same reason.
          <span>
            <Button
              label="Reprocess"
              icon="pi pi-refresh"
              className="p-button-text p-button-sm"
              onClick={
                reprocessable
                  ? () => onReprocessFile(p.id, p.displayName)
                  : undefined
              }
              disabled={!reprocessable}
              tooltip={reprocessTooltip}
              tooltipOptions={{ position: "top", showOnDisabled: true }}
            />
          </span>
        )}
        <Button
          icon="pi pi-file"
          className="p-button-rounded p-button-text"
          aria-label="Download original 835"
          tooltip="Download original 835"
          tooltipOptions={{ position: "top" }}
          onClick={() => onDownloadOriginal(p.id)}
        />
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-text p-button-danger"
          aria-label="Delete"
          tooltip="Delete"
          tooltipOptions={{ position: "top" }}
          onClick={() => onDeleteFile(p.id, p.displayName)}
        />
      </div>
    );
  };

  const piecesTemplate = (b: IEdiSplitBatch) => {
    const oversized = b.oversizedPieceCount ?? 0;
    let emptyNote: string | null = null;
    if (b.pieces.length === 0) {
      if (isSplitBatchActive(b) || b.retryPending) {
        emptyNote = "The pieces appear here once the split finishes.";
      } else if (b.status === "failed") {
        emptyNote = "No pieces were created.";
      } else {
        emptyNote = "This split has no pieces left.";
      }
    }
    return (
      <div className="px-3 py-2">
        {oversized > 0 && (
          <Message
            severity="warn"
            className="w-full justify-content-start mb-2"
            text={`${plural(
              oversized,
              "piece exceeds",
              "pieces exceed"
            )} the maximum because a single interchange was larger than it.`}
          />
        )}
        {emptyNote ? (
          <div className="text-600">{emptyNote}</div>
        ) : (
          <DataTable value={b.pieces} dataKey="id" size="small">
            <Column field="splitIndex" header="#" style={{ width: "3rem" }} />
            <Column field="displayName" header="Display name" />
            <Column header="Records" body={pieceRecordsBody} />
            <Column header="Status" body={pieceStatusBody} />
            <Column header="Last processed" body={pieceLastProcessedBody} />
            <Column
              header="Actions"
              body={(p: IEdiSplitPiece) => pieceActionsBody(p, b)}
              style={{ width: "14rem" }}
            />
          </DataTable>
        )}
      </div>
    );
  };

  if (isLoading) return <div>Loading…</div>;

  return (
    <div>
      <div className="flex justify-content-between align-items-center gap-3 mb-3">
        <span className="text-600">
          Cut a large 835 conversion file into smaller files you can process one
          at a time.
        </span>
        <Button
          label="Split a file"
          icon="pi pi-sitemap"
          className="p-button-raised"
          onClick={onSplitFile}
        />
      </div>

      {isError && (
        <small className="p-error block mb-2">
          Could not load the splits. Refresh the page to try again.
        </small>
      )}

      <DataTable
        value={batches}
        dataKey="id"
        paginator
        rows={10}
        size="small"
        responsiveLayout="scroll"
        expandedRows={expandedRows}
        onRowToggle={(e) => setExpandedRows(e.data as Record<string, boolean>)}
        rowExpansionTemplate={piecesTemplate}
        emptyMessage="No splits yet. Use “Split a file” or the split action on a file in the 835 Conversion Files tab."
      >
        <Column expander style={{ width: "3rem" }} />
        <Column header="Source file" body={sourceBody} />
        <Column header="Provider" body={providerBody} />
        <Column header="Max records / piece" body={maxRecordsBody} />
        <Column header="Pieces" body={piecesBody} />
        <Column header="Status" body={statusBody} />
        <Column header="Created by" body={createdByBody} />
        <Column header="Date" body={dateBody} />
        <Column
          header="Actions"
          body={batchActionsBody}
          style={{ minWidth: "18rem" }}
        />
      </DataTable>
    </div>
  );
}

export default EdiSplitFilesTab;
